import { toast } from "sonner";
import { getCurrentUsage, isToolSessionCounted } from "@/lib/usageGate";

// The tool page's usage gate, made reachable from plain functions such as downloadBlob (see
// components/DownloadGateRegistrar.tsx). Outside a tool page (a bundle run, a test) nothing is registered and a
// download is never held up.
let current: { toolId: string; gate: () => Promise<boolean> } | null = null;

export function registerDownloadGate(toolId: string | null, gate: (() => Promise<boolean>) | null) {
  current = toolId && gate ? { toolId, gate } : null;
}

/** Longest a Download waits for the usage check (under the 5 s promise, B-0309). A hung check must never leave the button doing nothing (B-0309). */
export const GATE_TIMEOUT_MS = 4000;
export const GATE_ERROR_MESSAGE = "Couldn't check today's uses. Please try the download again.";
export const GATE_REFUSED_MESSAGE = "Nothing was downloaded because today's use couldn't be confirmed. Please try again.";

// Counts the times the person was told something about a refused download (the pass dialog, or our own
// message), so a gate that said "no" after already showing the dialog is not followed by a second message.
let notices = 0;
export function noteUserNotified() {
  notices++;
}

/** A signed-in paying or admin session is never held back by the trial counter. */
function isPaidSession(): boolean {
  const s = getCurrentUsage();
  return !!s && (s.isPro || s.isAdmin);
}

/**
 * Runs a usage gate so that the answer is always acted on, never silence:
 *  - true: go on.
 *  - false, a rejection or no answer within `timeoutMs`: a paid or admin session goes on anyway (the trial
 *    counter does not apply to it); anyone else gets a visible message and no file. A plain `false` is
 *    already explained by the pass dialog, so the extra message only follows a `false` when `explainFalse`
 *    is set and nothing was shown meanwhile (a gate that refuses without saying why).
 * A late answer after the time-out is ignored, so one click can never save twice.
 */
export async function guardedGate(
  gate: () => Promise<boolean>,
  opts: { explainFalse?: boolean; timeoutMs?: number } = {},
): Promise<boolean> {
  // A paid or admin session never waits on the trial counter (B-0309): the check still runs in the background
  // (usage logging), but its answer, delay or failure cannot hold the file back.
  if (isPaidSession()) {
    try { Promise.resolve(gate()).catch(() => {}); } catch { /* ignored: the session is paid */ }
    return true;
  }
  const before = notices;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const outcome = await new Promise<"yes" | "no" | "error" | "timeout">((resolve) => {
    timer = setTimeout(() => resolve("timeout"), opts.timeoutMs ?? GATE_TIMEOUT_MS);
    try {
      Promise.resolve(gate()).then((ok) => resolve(ok ? "yes" : "no"), () => resolve("error"));
    } catch {
      resolve("error");
    }
  });
  if (timer) clearTimeout(timer);
  if (outcome === "yes") return true;
  // A refusal that already showed the pass dialog stands, whatever the session looks like.
  if (outcome === "no" && notices !== before) return false;
  if (isPaidSession()) return true;
  if (notices !== before) return false;
  if (outcome === "no" && !opts.explainFalse) return false;
  noteUserNotified();
  toast.error(outcome === "no" ? GATE_REFUSED_MESSAGE : GATE_ERROR_MESSAGE, { id: "download-gate" });
  return false;
}

/**
 * Runs `save` now when no gate applies or this tool's visit has already counted its use; otherwise asks the
 * gate first (which shows the pass dialog when today's trial uses are gone) and saves only if it allows.
 */
export function withDownloadGate(save: () => void): void {
  const g = current;
  if (!g || isToolSessionCounted(g.toolId)) {
    save();
    return;
  }
  // The use is taken here, at the moment the file is saved, and never for a file that was not. Whatever the
  // gate does (refuse, fail, hang) the button answers: it saves, or says why not.
  void guardedGate(g.gate, { explainFalse: true }).then((ok) => { if (ok) save(); });
}
